import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import {
    SettingType,
    Widget,
    Setting,
    SettingOption,
    ValidationMessage,
    ValidationMessageType,
} from '@appblocks/codegeneration/types';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import { cloneDeep } from 'lodash';

import { LCD_SCREEN_WIDGET_ROWS } from '@appblocks/platforms/PlatformUtils';

const widgetProperty: { [key: string]: string } = {
    ID: 'id',
    X: 'x',
    Y: 'y',
    W: 'w',
    H: 'h',
    TYPE: 'type',
    VARIABLE: 'variable',
    NAME: 'name',
    VALUES: 'values',
    LABEL: 'label',
    DISPLAY_LABEL: 'displayLabel',
    MIN: 'min',
    MAX: 'max',
    DECIMAL_PLACES: 'decimalPlaces',
    BOUNDS: 'bounds',
    HAS_INPUT: 'hasInput',
    ON_VALUE: 'onValue',
    OFF_VALUE: 'offValue',
    ON_LABEL: 'onLabel',
    OFF_LABEL: 'offLabel',
    BUTTON_LABEL: 'buttonLabel',
    IS_ON: 'isOn',
    IS_HORIZONTAL: 'isHorizontal',
    CUSTOM_FN: 'customFn',
    IMG: 'img',
    FONT_SIZE: 'fontSize',
    ALIGNMENT: 'alignment',
    ORIENTATION: 'orientation',
    FONT: 'font',
    IMG_WIDTH: 'imgWidth',
    IMG_HEIGHT: 'imgHeight',
    COLOR: 'color',
    FILL_COLOR: 'fillColor',
    BACKGROUND_COLOR: 'backgroundColor',
    TEXT: 'text',
};

export const sizes: {
    [key: string]: {
        minW: number,
        minH: number,
        defaultW: number,
        defaultH: number,
    }
} = {
    line_widget: {
        minW: 3,
        minH: 5,
        defaultW: 3,
        defaultH: 5,
    },
    bar_widget: {
        minW: 2,
        minH: 2,
        defaultW: 2,
        defaultH: 2,
    },
    gauge_widget: {
        minW: 3,
        minH: 4,
        defaultW: 3,
        defaultH: 4,
    },
    card_widget: {
        minW: 2,
        minH: 3,
        defaultW: 2,
        defaultH: 3,
    },
    text_widget: {
        minW: 2,
        minH: 3,
        defaultW: 2,
        defaultH: 3,
    },
    slider_widget: {
        minW: 2,
        minH: 2,
        defaultW: 2,
        defaultH: 2,
    },
    switch_widget: {
        minW: 2,
        minH: 3,
        defaultW: 2,
        defaultH: 3,
    },
    button_widget: {
        minW: 2,
        minH: 3,
        defaultW: 2,
        defaultH: 3,
    },
    lcd_graph_widget: {
        minW: 40,
        minH: 20,
        defaultW: 200,
        defaultH: 100,
    },
    lcd_card_widget: {
        minW: 1,
        minH: 1,
        defaultW: 1,
        defaultH: 1,
    },
    lcd_text_widget: {
        minW: 10,
        minH: 10,
        defaultW: 100,
        defaultH: 20,
    },
    basewidget: {
        minW: 2,
        minH: 2,
        defaultW: 2,
        defaultH: 2,
    },
    lcd_image_widget: {
        minW: 5,
        minH: 5,
        defaultW: 80,
        defaultH: 80,
    },
};

const allWidgetSettings = [
    {
        id: 'TYPE',
        name: 'Type',
        settingType: SettingType.OPTION,
        required: true,
        default: 'card',
    },
    {
        id: 'VARIABLE',
        name: 'Variable',
        settingType: SettingType.OPTION,
        required: true,
        default: '',
    },
    {
        id: 'X',
        name: 'X',
        settingType: SettingType.HIDDEN,
        required: true,
        default: '0',
    },
    {
        id: 'Y',
        name: 'Y',
        settingType: SettingType.HIDDEN,
        required: true,
        default: '0',
    },
    {
        id: 'W',
        name: 'W',
        settingType: SettingType.HIDDEN,
        required: true,
        default: '4',
    },
    {
        id: 'H',
        name: 'H',
        settingType: SettingType.HIDDEN,
        required: true,
        default: '4',
    },
    {
        id: 'NAME',
        name: 'Name',
        settingType: SettingType.STRING,
        required: true,
        default: 'widget',
    },
    {
        id: 'LABEL',
        name: 'Label',
        settingType: SettingType.STRING,
        required: true,
        default: 'widget_label',
    },
    {
        id: 'MIN',
        name: 'Min',
        settingType: SettingType.NUMBER,
        required: true,
        default: '0',
    },
    {
        id: 'MAX',
        name: 'Max',
        settingType: SettingType.NUMBER,
        required: true,
        default: '100',
    },
    {
        id: 'DECIMAL_PLACES',
        name: 'Decimal Places',
        settingType: SettingType.NUMBER,
        required: true,
        default: '0',
    },
    {
        id: 'VALUES',
        name: 'Values',
        settingType: SettingType.NUMBER,
        required: true,
        default: '50',
    },
    {
        id: 'CUSTOM_FN',
        name: 'Custom Function',
        settingType: SettingType.STRING,
        required: true,
        default: '',
    },
    {
        id: 'BOUNDS',
        name: 'Bounds',
        settingType: SettingType.BOOLEAN,
        required: true,
        default: '1',
    },
    {
        id: 'ON_VALUE',
        name: 'On Value',
        settingType: SettingType.NUMBER,
        required: true,
        default: '1',
    },
    {
        id: 'OFF_VALUE',
        name: 'Off Value',
        settingType: SettingType.NUMBER,
        required: true,
        default: '0',
    },
    {
        id: 'ON_LABEL',
        name: 'On Label',
        settingType: SettingType.STRING,
        required: true,
        default: 'On',
    },
    {
        id: 'BUTTON_LABEL',
        name: 'Button Label',
        settingType: SettingType.STRING,
        required: true,
        default: 'Send',
    },
    {
        id: 'OFF_LABEL',
        name: 'Off Label',
        settingType: SettingType.STRING,
        required: true,
        default: 'Off',
    },
    {
        id: 'IS_ON',
        name: 'Is On',
        settingType: SettingType.BOOLEAN,
        required: true,
        default: '1',
    },
    {
        id: 'TEXT',
        name: 'Text',
        settingType: SettingType.STRING,
        required: true,
        default: '',
        helpText: 'The initial text. It can be changed at runtime using the "LCD Text Widget" block.',
    },
    {
        id: 'FILL_COLOR',
        name: 'Fill Color',
        settingType: SettingType.COLOR,
        required: true,
        default: '#00FF00',
    },
    {
        id: 'COLOR',
        name: 'Color',
        settingType: SettingType.COLOR,
        required: true,
        default: '#ffffff',
    },
    {
        id: 'BACKGROUND_COLOR',
        name: 'Background Color',
        settingType: SettingType.COLOR,
        required: true,
        default: '#000000',
    },
    {
        id: 'DISPLAY_LABEL',
        name: 'Display Label',
        settingType: SettingType.BOOLEAN,
        required: true,
        default: '1',
    },
    {
        id: 'ALIGNMENT',
        name: 'Text Alignment',
        settingType: SettingType.OPTION,
        default: 'PL_LCD_TEXT_ALIGNMENT_TOP_LEFT',
        options: [
            // {
            //     name: 'Left',
            //     value: 'PL_LCD_TEXT_ALIGNMENT_TOP_LEFT',
            // },
            // {
            //     name: 'Center',
            //     value: 'PL_LCD_TEXT_ALIGNMENT_TOP_CENTER',
            // },
            // {
            //     name: 'Right',
            //     value: 'PL_LCD_TEXT_ALIGNMENT_TOP_RIGHT',
            // },
            {
                name: 'Left',
                value: 'PL_LCD_TEXT_ALIGNMENT_MIDDLE_LEFT',
            },
            {
                name: 'Center',
                value: 'PL_LCD_TEXT_ALIGNMENT_MIDDLE_CENTER',
            },
            {
                name: 'Right',
                value: 'PL_LCD_TEXT_ALIGNMENT_MIDDLE_RIGHT',
            },
            // {
            //     name: 'Bottom Left',
            //     value: 'PL_LCD_TEXT_ALIGNMENT_BOTTOM_LEFT',
            // },
            // {
            //     name: 'Bottom Center',
            //     value: 'PL_LCD_TEXT_ALIGNMENT_BOTTOM_CENTER',
            // },
            // {
            //     name: 'Bottom Right',
            //     value: 'PL_LCD_TEXT_ALIGNMENT_BOTTOM_RIGHT',
            // },
        ],
    },
    {
        id: 'ORIENTATION',
        name: 'Text Orientation',
        settingType: SettingType.OPTION,
        default: 'PL_LCD_TEXT_ORIENTATION_0',
        options: [
            {
                name: 'At 0 Degrees',
                value: 'PL_LCD_TEXT_ORIENTATION_0',
            },
            {
                name: 'At 90 degrees',
                value: 'PL_LCD_TEXT_ORIENTATION_90',
            },
            {
                name: 'At 180 degrees',
                value: 'PL_LCD_TEXT_ORIENTATION_180',
            },
            {
                name: 'At 270 degrees',
                value: 'PL_LCD_TEXT_ORIENTATION_270',
            },
        ],
    },
    {
        id: 'FONT',
        name: 'Font Size',
        settingType: SettingType.OPTION,
        required: true,
        default: 'LCD_STD_FONT',
        options: [
            {
                name: 'Standard Font',
                value: 'LCD_STD_FONT',
            },
            {
                name: 'Small Font',
                value: 'LCD_SMALL_FONT',
            },
        ],
    },
    {
        id: 'IMG',
        name: 'Image',
        settingType: SettingType.OPTION,
        required: false,
        default: '',
        refId: 'lcd_image',
        helpText: 'The initial image. It can be changed at runtime using the "LCD Image Widget" block.',
    },
    {
        id: 'IMG_WIDTH',
        name: 'Image Width',
        settingType: SettingType.NUMBER,
        required: true,
        default: '0',
    },
    {
        id: 'IMG_HEIGHT',
        name: 'Image Height',
        settingType: SettingType.NUMBER,
        required: true,
        default: '0',
    },
    {
        id: 'HAS_INPUT',
        name: 'Has Input',
        settingType: SettingType.BOOLEAN,
        required: true,
        default: '0',
    },
    {
        id: 'IS_HORIZONTAL',
        name: 'Orientation',
        settingType: SettingType.OPTION,
        required: true,
        default: '1',
        options: [
            {
                name: 'Horizontal',
                value: '1',
            },
            {
                name: 'Vertical',
                value: '0',
            },
        ],
    },
];

export default class BaseWidget extends BaseFeature {
    minW: number;

    minH: number;

    constructor() {
        super();
        this.name = 'basewidget';
        this.id = 'basewidget';
        this.minW = sizes[this.id].minW;
        this.minH = sizes[this.id].minH;

        const settings = [
            {
                id: 'X',
                name: 'X',
                settingType: SettingType.HIDDEN,
                required: true,
                default: '0',
            },
            {
                id: 'Y',
                name: 'Y',
                settingType: SettingType.HIDDEN,
                required: true,
                default: '0',
            },
            {
                id: 'W',
                name: 'W',
                settingType: SettingType.HIDDEN,
                required: true,
                default: '4',
            },
            {
                id: 'H',
                name: 'H',
                settingType: SettingType.HIDDEN,
                required: true,
                default: '4',
            },
            {
                id: 'VISIBLE',
                name: 'Visibility',
                settingType: SettingType.OPTION,
                required: true,
                default: '1',
                options: [
                    {
                        name: 'Visible',
                        value: '1',
                    },
                    {
                        name: 'Hidden',
                        value: '0',
                    },
                ],
            },
            // {
            //     id: 'NAME',
            //     name: 'Name',
            //     settingType: SettingType.STRING,
            //     required: true,
            //     default: 'widget',
            // },
        ];

        this.settings = this.settings.concat(settings);
    }

    getWidgetObject(): Widget {
        const project = this.project;
        const parent = project.getParent(this);
        let screen = 0;
        if (parent !== undefined) {
            const displayFeature = project.getParent(parent);
            if (displayFeature !== undefined) {
                for (let i = 0; i < displayFeature.settings.length; i += 1) {
                    if (displayFeature.settings[i].id === 'SCREENS') {
                        for (let j = 0; j < displayFeature.settings[i].value.length; j += 1) {
                            if (displayFeature.settings[i].value[j].getID() === parent.getID()) {
                                screen = j;
                                break;
                            }
                        }
                    }
                }
            }
        }

        const widget: Widget = {
            name: this.name,
            x: Number(this.getSettingValue('X')),
            y: Number(this.getSettingValue('Y')),
            w: Number(this.getSettingValue('W')),
            minW: this.minW,
            minH: this.minH,
            h: Number(this.getSettingValue('H')),
            id: this.getSettingValue('ID'),
            type: this.id,
            screen,
        };

        for (let i = 0; i < this.settings.length; i += 1) {
            const setting = this.settings[i];
            if (setting.settingType === SettingType.HIDDEN) {
                continue;
            }
            if (widgetProperty[setting.id] !== undefined) {
                if (widget.type === 'lcd_image_widget'
                    && setting.value !== ''
                    && setting.id === 'IMG') {
                    const img = project.getFeature(this.getSettingValue('IMG'));
                    if (!img) continue;
                    widget[widgetProperty[setting.id]] = img.getSettingValue('IMG') || '';
                    widget.imgWidth = img.getSettingValue('WIDTH') || 0;
                    widget.imgHeight = img.getSettingValue('HEIGHT') || 0;
                    continue;
                }
                switch (setting.settingType) {
                    case SettingType.NUMBER:
                        widget[widgetProperty[setting.id]] = Number(setting.value);
                        break;
                    // case SettingType.BOOLEAN:
                    // widget[widgetProperty[setting.id]] = setting.value === '1';
                    // break;
                    default:
                        widget[widgetProperty[setting.id]] = setting.value;
                        break;
                }
            }
        }
        return widget;
    }

    getWidgetSettings(params: string[]): Setting[] {
        const settings: Setting[] = [];
        for (let i = 0; i < params.length; i += 1) {
            const setting = allWidgetSettings.find(s => s.id === params[i]);
            if (setting) {
                const newSetting = cloneDeep(setting);
                if (newSetting.id === 'VARIABLE') {
                    newSetting.refId = 'variable';
                    if (this.id === 'button_widget') {
                        newSetting.name = 'Command';
                        newSetting.refId = 'command';
                    }
                }
                if (newSetting.id === 'NAME') {
                    newSetting.default = this.id.replace('lcd_', '');
                }
                if (params.includes(newSetting.id)) {
                    settings.push(newSetting);
                }
            }
        }
        return settings;
    }

    initSettings(project?: any, parentFeature?: any, defaultValue?: string): void {
        for (let i = 0; i < this.settings.length; i += 1) {
            if (this.settings[i].id === 'W') {
                this.settings[i].default = sizes[this.id].defaultW;
            }
            if (this.settings[i].id === 'H') {
                this.settings[i].default = sizes[this.id].defaultH;
            }
            if (this.settings[i].id === 'LABEL') {
                this.settings[i].default = this.name.replace(' Widget', '');
            }
            if (this.settings[i].id === 'VARIABLE') {
                if (this.id === 'button_widget') {
                    this.settings[i].refId = 'command';
                } else {
                    this.settings[i].refId = 'variable';
                }
            }
        }
        super.initSettings(project, parentFeature, 'NAME');
        // if (this.id.indexOf('lcd_') >= 0) {
        //     for (let i = 0; i < this.settings.length; i += 1) {
        //         if (this.settings[i].id === 'NAME') {
        //             this.settings[i].value = this.name;
        //         }
        //     }
        // }
    }

    // setSettingValue(settingId: string, settingValue: any): void {
    //     let value = settingValue;
    //     if (settingId === 'W'
    //         || settingId === 'H') {
    //         for (let i = 0; i < this.settings.length; i += 1) {
    //             const setting = this.settings[i];
    //             if (setting.id === settingId && setting.default > settingValue) {
    //                 value = setting.default;
    //             }
    //         }
    //     }
    //     super.setSettingValue(settingId, value);
    // }

    validate(): ValidationMessage[] {
        const { project } = this;
        const errors: ValidationMessage[] = [];
        const variableId = this.getSettingValue('VARIABLE');
        const type = this.getSettingValue('TYPE');
        const label = this.getSettingValue('LABEL');
        const min = this.getSettingValue('MIN');
        const max = this.getSettingValue('MAX');
        const id = this.getID();

        // determine if variable is required
        let variableRequired = false;
        const settings = this.settings;
        for (let i = 0; i < settings.length; i += 1) {
            if (settings[i].id === 'VARIABLE') {
                variableRequired = true;
                break;
            }
        }

        if (variableRequired && variableId === '') {
            errors.push({
                message: `Variable not set for ${this.id}(${label})`,
                messageType: ValidationMessageType.ERROR,
                featureId: id,
                settingId: 'VARIABLE',
            });
        }

        if (variableId !== '' && variableId !== undefined) {
            const variable = project.getFeature(variableId);
            if (!variable) {
                errors.push({
                    message: `Variable not set for ${type}
                    (${label})`,
                    messageType: ValidationMessageType.ERROR,
                    featureId: id,
                    settingId: 'VARIABLE',
                });
            } else {
                const legalMin = variable.getSettingValue('MINIMUM');
                const legalMax = variable.getSettingValue('MAXIMUM');
                if (Number(min) < Number(legalMin) && legalMin !== '') {
                    errors.push({
                        message: `Minimum value for ${type}
                        (${label}) must be equal to or more than ${legalMin}`,
                        messageType: ValidationMessageType.ERROR,
                        featureId: id,
                        settingId: 'MIN',
                    });
                }
                if (Number(max) > Number(legalMax) && legalMax !== '') {
                    errors.push({
                        message: `Maximum value for ${type}
                        (${label}) must be less than or equal to ${legalMax}`,
                        messageType: ValidationMessageType.ERROR,
                        featureId: id,
                        settingId: 'MAX',
                    });
                }
            }
        }

        if (this.id.indexOf('lcd_') >= 0) {
            const y = this.getSettingValue('Y');
            const h = this.getSettingValue('H');
            if (Number(y) + Number(h) > LCD_SCREEN_WIDGET_ROWS) {
                errors.push({
                    messageType: ValidationMessageType.ERROR,
                    message: 'Widget is at least partially out of bounds. Move it higher so that it fits on the LCD screen.',
                    featureId: id,
                });
            }
        }

        return errors;
    }

    getSettingOptions(settingId: string): SettingOption[] | undefined {
        const project: ProjectConfiguration = this.project;
        const parent = project.getParent(this);
        if (!parent) {
            return super.getSettingOptions(settingId);
        }
        let options: SettingOption[] = [];
        switch (settingId) {
            case 'TYPE':
                if (parent.id === 'lcd') {
                    options = options.concat([
                        {
                            name: 'Graph',
                            value: 'lcd_graph_widget',
                        },
                        {
                            name: 'Card',
                            value: 'lcd_card_widget',
                        },
                        {
                            name: 'Text',
                            value: 'lcd_text_widget',
                        },
                        {
                            name: 'Image',
                            value: 'lcd_image_widget',
                        },
                    ]);
                } else {
                    options = options.concat([
                        {
                            name: 'Graph',
                            value: 'line_widget',
                        },
                        {
                            name: 'Bar',
                            value: 'bar_widget',
                        },
                        {
                            name: 'Gauge',
                            value: 'gauge_widget',
                        },
                        {
                            name: 'Card',
                            value: 'card_widget',
                        },
                        {
                            name: 'Text Input',
                            value: 'text_widget',
                        },
                        {
                            name: 'Slider',
                            value: 'slider_widget',
                        },
                        {
                            name: 'Switch',
                            value: 'switch_widget',
                        },
                        {
                            name: 'Button',
                            value: 'button_widget',
                        },
                    ]);
                }
                break;
            case 'VARIABLE':
            case 'IMG':
            default:
                return super.getSettingOptions(settingId);
        }
        return options;
    }
}
